import { useEffect, useState, MouseEvent } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { NewFileDialog } from "@/components/NewFileDialog";
import { FileText, Trash2, Pencil, Download } from "lucide-react";
import { useUser } from "../../context/UserContext";
import { EditFileForm } from "../../components/EditFileForm";
import { ExportDialog } from "@/components/ExportDialog";
import { VersionNavigation } from "@/components/VersionNavigation";

interface Game {
  id: number;
  name: string;
}

interface TranslationFile {
  id: number;
  name: string;
  url?: string | null;
  stats: {
    total: number;
    waiting_for_translation: number;
    submitted: number;
    accepted: number;
    // optional character-level statistics (if provided by the API)
    total_chars?: number;
    waiting_chars?: number;
    submitted_chars?: number;
    accepted_chars?: number;
  };
}

interface Version {
  id: number;
  version_number: string;
  translation_files: TranslationFile[];
}

interface VersionDetailsData {
  game: Game;
  version: Version;
}

const VersionDetails = () => {
  const { gameId, versionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<VersionDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleEditClick = (e: MouseEvent, fileId: number) => {
    e.stopPropagation();
    setEditingFileId(editingFileId === fileId ? null : fileId);
  };

  const handleSaveFile = async (fileId: number, newName: string) => {
    try {
      const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${fileId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({ name: newName }),
      });

      if (!response.ok) {
        throw new Error('A fájl mentése sikertelen.');
      }

      const updatedFile = await response.json();

      setData(prevData => {
        if (!prevData) return null;
        return {
          ...prevData,
          version: {
            ...prevData.version,
            translation_files: prevData.version.translation_files.map(f =>
              f.id === fileId ? { ...f, name: updatedFile.name } : f
            ),
          },
        };
      });
    } catch (err) {
      console.error(err);
      alert('Hiba történt a fájl mentése során.');
    } finally {
      setEditingFileId(null);
    }
  };

  const handleDeleteFile = async (e: MouseEvent, fileId: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (window.confirm("Biztosan törölni szeretnéd ezt a fájlt?")) {
      try {
        const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${fileId}`, {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          },
        });
        if (!response.ok) throw new Error("A fájl törlése sikertelen.");

        setData(prevData => {
          if (!prevData) return null;
          return { ...prevData, version: { ...prevData.version, translation_files: prevData.version.translation_files.filter(f => f.id !== fileId) } };
        });
      } catch (err) {
        console.error(err);
        alert("Hiba történt a fájl törlése során.");
      }
    }
  };

  const handleDeleteAllFiles = async () => {
    if (window.confirm("Biztosan törölni szeretnéd az összes fájlt ehhez a verzióhoz? Ezt a műveletet nem lehet visszavonni.")) {
      try {
        const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files`, {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          },
        });
        if (!response.ok) throw new Error("A fájlok törlése sikertelen.");

        setData((prevData: VersionDetailsData | null) => {
          if (!prevData) return null;
          return {
            ...prevData,
            version: {
              ...prevData.version,
              translation_files: []
            }
          };
        });

      } catch (err) {
        console.error(err);
        alert("Hiba történt a fájlok törlése során.");
      }
    }
  };

  useEffect(() => {
    const fetchVersionDetails = async () => {
      try {
        setLoading(true);
        const [versionResponse, gameResponse] = await Promise.all([
          fetch(`/api/games/${gameId}/versions/${versionId}`),
          fetch(`/api/games/${gameId}`)
        ]);

        if (!versionResponse.ok) {
          throw new Error("A verzió részleteinek betöltése sikertelen.");
        }
        if (!gameResponse.ok) {
          throw new Error("A játék részleteinek betöltése sikertelen.");
        }
        const versionData = await versionResponse.json();
        const gameData = await gameResponse.json();

        const compositeData: VersionDetailsData = {
          game: gameData,
          version: {
            ...versionData,
            version_number: versionData.name,
            translation_files: versionData.translation_files || []
          }
        };
        setData(compositeData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Ismeretlen hiba történt.");
      } finally {
        setLoading(false);
      }
    };

    fetchVersionDetails();
  }, [gameId, versionId]);

  const handleFileAdded = (newFile: TranslationFile) => {
    setData(prevData => {
      if (!prevData) return null;
      return {
        ...prevData,
        version: { ...prevData.version, translation_files: [...prevData.version.translation_files, newFile] }
      };
    });
  };

  const handleRowClick = (fileId: number) => {
    if (editingFileId === fileId) return;
    navigate(`/games/${gameId}/versions/${versionId}/files/${fileId}/statistics`);
  };

  if (loading && !data) return <div>Betöltés...</div>;
  if (error) return <div>Hiba: {error}</div>;
  if (!data) return <div>A verzió nem található.</div>;

  const { game, version } = data;

  return (
    <div>
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/games">Játékok</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${game.id}`}>{game.name}</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>Verzió: {version.version_number}</BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex gap-2 mb-6 flex-wrap">
        <VersionNavigation gameId={gameId} versionId={versionId} />
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl font-bold">Verzió: {version.version_number}</h1>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Fájlok a(z) {version.version_number} verzióhoz</CardTitle>
          <div className="flex items-center gap-2">
            {(user?.admin || user?.translater_admin) && (
              <Button
                size="sm"
                onClick={() => setIsExportDialogOpen(true)}
              >
                <Download className="h-4 w-4 mr-2" />
                Exportálás
              </Button>
            )}
            {user?.admin && version.translation_files.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleDeleteAllFiles}>
                <Trash2 className="h-4 w-4 mr-2" />
                Összes fájl törlése
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {version.translation_files && version.translation_files.length > 0 ? (
            <ul className="space-y-3">
              {version.translation_files.slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })).map((file) => {
                const progressPercentage = file.stats?.total ? (file.stats.accepted / file.stats.total) * 100 : 0;
                const charsPercentage = (file.stats && typeof file.stats.total_chars !== 'undefined' && file.stats.total_chars > 0)
                  ? ((file.stats.accepted_chars ?? 0) / file.stats.total_chars) * 100
                  : null;
                return (
                  <li key={file.id}>
                    <div
                      className="p-4 border rounded-lg flex items-center justify-between hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => handleRowClick(file.id)}
                    >
                      <div className="flex items-center gap-4">
                        <FileText className="h-6 w-6 text-muted-foreground" />
                        <span className="font-semibold text-lg">{file.name}</span>
                      </div>
                      {file.stats && (
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>Összes: {file.stats.total}</span>
                          <span>Fordításra vár: {file.stats.waiting_for_translation}</span>
                          <span>Beküldve: {file.stats.submitted}</span>
                          <span>Elfogadva: {file.stats.accepted}</span>
                          {typeof file.stats.total_chars !== 'undefined' && (
                            <span>Karakterek: {file.stats.accepted_chars ?? 0} / {file.stats.total_chars}</span>
                          )}
                        </div>
                      )}
                      {user?.admin && (
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={(e) => handleEditClick(e, file.id)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="icon" onClick={(e) => handleDeleteFile(e, file.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 transition-all duration-300"
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-300"
                          style={{ width: `${charsPercentage !== null ? charsPercentage : 0}%` }}
                        ></div>
                      </div>
                      <div className="grid grid-cols-2 text-xs text-muted-foreground mt-1 px-2">
                        <div className="text-left">Sorok alapján: {file.stats ? `${progressPercentage.toFixed(1)}%` : '0%'}</div>
                        <div className="text-right">Karakterek alapján: {charsPercentage !== null ? `${charsPercentage.toFixed(1)}%` : '—'}</div>
                      </div>
                    </div>
                    {editingFileId === file.id && (
                      <EditFileForm
                        file={file}
                        onSave={handleSaveFile}
                        onCancel={() => setEditingFileId(null)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-muted-foreground text-center py-8">Nincsenek még fájlok feltöltve ehhez a verzióhoz.</p>
          )}
          <div className="mt-6">
            {user?.admin && (
              <NewFileDialog gameId={Number(gameId)} versionId={Number(versionId)} onFileAdded={handleFileAdded} />
            )}
          </div>
        </CardContent>
      </Card>

      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        gameId={Number(gameId)}
        versionId={Number(versionId)}
      />
    </div>
  );
};

export default VersionDetails;